import React from 'react';
import { usePlanFeatures } from '../hooks/usePlanFeatures';
import type { PlanFeatures } from '../lib/planFeatures';
import type { PlanId } from '@foundlio/pricing';
import PlanUpsell from './ui/PlanUpsell';
import { MARKS } from '../lib/marks';

interface PlanGateProps {
  feature: keyof PlanFeatures;
  children: React.ReactNode;
}

/**
 * What a locked route shows instead of its page. The gated feature is the
 * subject of the drawing — a padlock would sell nothing.
 */
const GATED: Record<
  keyof PlanFeatures,
  { mark: string; requiredPlan: PlanId; title: string; description: string; includes: string[] }
> = {
  seo: {
    mark: MARKS.seo,
    requiredPlan: 'professional',
    title: 'Search keyword tracking',
    description:
      'See where you rank for the searches your customers actually type, and what to fix first.',
    includes: [
      'Keyword positions, tracked weekly',
      'Google Business Profile setup',
      'Advanced on-page and local SEO',
    ],
  },
  bookingsEnable: {
    mark: MARKS.bookings,
    requiredPlan: 'professional',
    title: 'Online booking',
    description: 'Let customers pick a slot on your site instead of waiting for a callback.',
    includes: ['Bookings straight from your website', 'Your hours and buffer times', 'Email alerts for each request'],
  },
  autoContent: {
    mark: MARKS.autoContent,
    requiredPlan: 'professional',
    title: 'Automatic content updates',
    description:
      'Fresh pages and posts published for you every month — the thing search engines reward.',
    includes: ['Monthly content updates', 'Written and scheduled for you', 'Published without you lifting a finger'],
  },
};

// Route guard: a plan that lacks the feature gets the upsell in place of the
// page, not a redirect — a bounce back to the dashboard reads as a bug and
// sells nothing. Renders nothing while the subscription loads to avoid a flash.
const PlanGate: React.FC<PlanGateProps> = ({ feature, children }) => {
  const { features, isLoading } = usePlanFeatures();

  if (isLoading) return null;
  if (!features[feature]) {
    const copy = GATED[feature];
    return (
      <div className="py-6">
        <PlanUpsell variant="page" {...copy} />
      </div>
    );
  }

  return <>{children}</>;
};

export default PlanGate;
